import {IIntegerRange, IRect, IRectMargins} from '@colibrio/colibrio-reader-framework/colibrio-core-base';
import {ILocator, ILocatorData} from '@colibrio/colibrio-reader-framework/colibrio-core-locator';
import {
    IPublicationMetadataData,
} from '@colibrio/colibrio-reader-framework/colibrio-core-publication-base';
import {
    ContainerContentBlockType,
    ContentPositionTimelineUnit,
    IActiveRendererChangedEngineEvent, IContentBlock,
    IContentBlockData,
    IContentLocation,
    IContentPositionTimeline,
    IKeyboardEngineEvent,
    IMouseEngineEvent,
    INavigationEndedEngineEvent,
    INavigationIntentEngineEvent,
    INavigationStartedEngineEvent,
    IPageProgressionTimeline,
    IPageProgressionTimelineEngineEvent,
    IPointerEngineEvent,
    IPublicationStyleFontDefaults,
    IPublicationStylePalette,
    IPublicationStyleTextAlignmentOptions,
    IReaderPublicationStorageState,
    IReaderViewAnnotationEngineEvent,
    IReaderViewAnnotationMouseEngineEvent,
    IReaderViewAnnotationPointerEngineEvent,
    IReaderViewEngineEvent,
    IReaderViewOptions,
    IRendererOptions,
    ISelectionChangedEngineEvent, ISwipeNavigationGestureProgressEngineEventData,
    ISyncMediaSegmentData,
    ISyncMediaTimeline, ISyncMediaTimelineRangeData,
    ITransformData,
    ITransformRectZoomOptions, ITtsSynthesizer,
    IVisibleContentRectData,
    MediaContentBlockType,
    NavigationCollectionType, SyncMediaRangePlaybackRemoveReason, SyncMediaRangeSeekBehavior,
    TextContentBlockType,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import {
    IPublicationSearchIndexContentNodeData,
    IPublicationSearchIndexData,
} from '../utils/PublicationSearchIndexDataFactory';
import {VanillaReaderShelfItem} from "../VanillaReaderBookshelf/VanillaReaderShelfItem";
import {IntRange} from "../utils/IntRange";
import {VanillaReaderGuidedReadingStep} from "../VanillaReaderGuidedReadingPlayer/VanillaReaderGuidedReadingTimeline";
import {MediaType} from "@colibrio/colibrio-reader-framework/colibrio-core-io-base";
import {VanillaReaderUiDialog} from "../VanillaReaderUI/VanillaReaderUiDialog";
import {VanillaReaderLandmark} from "./VanillaReaderLandmark";
import {ISpeechSynthesisVoiceCollection} from "../utils/WebSpeechUtils";

/*
*
* # VanillaReaderModel.ts
*
* ## RESPONSIBILITIES
*
* This file holds all model types (mostly interfaces) that are used throughout the code. It is the common "dictionary"
* of data types that used by all VanillaReader components to manage its runtime state, as well as its serialised state.
*
* The exception are the `VanillaReaderAppEvent_*` types which are located in the `VanillaReaderEventBus.ts` file.
*
* */

/*
* # VanillaReaderNavigationType
*
* Used by the application components to signal what type of navigation action that is intended, or that has occurred.
*
**/
export enum VanillaReaderNavigationType {
    GOTO = 'vanillareader_navigationtype_goto',
    TIMELINEPOSITION = 'vanillareader_navigationtype_timelineposition',
    GOTOSTART = 'vanillareader_navigationtype_gotostart',
    NEXT = 'vanillareader_navigationtype_next',
    PREVIOUS = 'vanillareader_navigationtype_previous',
    EXTERNAL = 'vanillareader_navigationtype_external'
}

/*
* # VanillaReaderRendererNames
*
* A list of the renderers used by the application. The string values correspond to the different class names of the
* Colibrio Framework IRenderer implementations.
*
* The only entry that does not match an instance in the `ReaderView` is `RESPONSIVE` which acts more like a flag that
* tells the `ReaderView` to use the `ResponsiveViewRules`. These rules are added when the renderers are added.
*
* NOTE: Do not change these values without really considering how you will migrate user settings as these string values
* will have been stored on the users device.
*
* ## RELATED TYPES
*
* - IRenderer
* - IReaderView
*
**/
export enum VanillaReaderRendererNames {
    RESPONSIVE = '__responsive__',
    FLIPBOOK = 'FlipBookRenderer',
    STACK = 'StackRenderer',
    SCROLL = 'SingleDocumentScrollRenderer',
    SWIPE_SINGLE = 'SinglePageSwipeRenderer',
    SWIPE_SPREAD = 'SpreadSwipeRenderer'
}

/*
* # IVanillaReaderHighlight
*
* This interface defines the data that is used to describe a highlight annotation in the VanillaReader. This data is used
* by for example the highlight annotation layer and the UI.
*
**/
export interface IVanillaReaderHighlightData {
    _id?: string,
    color: string,
    comment: string,
    locator: string,
    selectionText: string,
    publicationId?: string,
    bookSection?: string,
    dateCreated?: number,
    dateUpdated?: number,
}

/*
* # IVanillaReaderBookmark
*
* This interface defines the data that is used to describe a bookmark annotation in the VanillaReader. This data is used
* by for example the bookmark annotation layer and the UI.
*
**/
export interface IVanillaReaderBookmarkData {
    _id?: string,
    locator: string,
    publicationId?: string,
    color?: string,
    text?: string,
    bookSection?: string,
    dateCreated?: number,
}

/*
* # IVanillaReaderSearchResultNodes
*
* This interface describes a content node in a `IVanillaReaderPublicationSearchResultItem`. A node will often map to
* an HTML element, such as a `<p>` element, in the document content. A search result item can span more than one of these
* node, so you will find a `nodes` array in the parent `IVanillaReaderPublicationSearchResultItem` instance.
*
* ## RELATED TYPES
*
* - IVanillaReaderPublicationSearchResultItem
*  - VanillaReaderPublicationSearch
* - IPublicationSearchIndexData
*
**/
export interface IVanillaReaderSearchResultNodes {
    locator: string,
    startOffsetInIndexedNodeText: number,
    endOffsetInIndexedNodeText: number,
    indexedNodeText?: string,
    searchIndexNode: IPublicationSearchIndexContentNodeData
}

/*
* # IVanillaReaderPublicationSearchResultItem
*
* This interface describes a search result returned by the `VanillaReaderPublicationSearch` class.
*
*  A node will often map to an HTML element, such as a `<p>` element, in the document content. A search result item can
* span more than one content nodes, so you iterate over each `IVanillaReaderSearchResultNodes` to get all te content
* that is part of the result.
*
* ## RELATED TYPES
*
* - IVanillaReaderSearchResultNodes
* - VanillaReaderPublicationSearch
* - IPublicationSearchIndexData
*
**/
export interface IVanillaReaderPublicationSearchResultItem {
    bookSection: string | undefined,
    nodes: IVanillaReaderSearchResultNodes[],
    searchTermStartOffsetInIndexedContent: number,
    searchTermEndOffsetInIndexedContent: number,
    locator: string,
    intersectingNodeText?: string,
    readerDocumentIndexInSpine?: number,
}

/*
* # IVanillaReaderNavigationTreeData
*
* This interface describes the entire publication navigation structure for a `VanillaReaderPublication` (and in turn a
* Colibrio Framework IReaderPublication of course).
*
* This data is used for example by the UI to render the Table of Contents, but also when looking up navigation items
* in relation to positions within the document contents.
*
* The reason for the application specific type will be clear when you have a look at the `IVanillaReaderNavigationItem`
* type that has a lot of extra data in comparison to its Colibrio Framework counterpart.
*
* ## RELATED TYPES
*
* - IReaderPublicationNavigation
* - IVanillaReaderNavigationItem
*
**/
export interface IVanillaReaderNavigationTreeData {
    collections: IVanillaReaderNavigationCollection[],
    items: IVanillaReaderNavigationItem[]
}

/*
* # IVanillaReaderNavigationCollection
*
* This interface describes one type of navigation collection. The types of collections are the same as the once defined
* in the Colibrio Framework `NavigationCollectionType` enum, such as `TOC`, `PAGE_LIST`, `LIST_OF_ILLUSTRATIONS` etc.
*
* ## RELATED TYPES
*
* - NavigationCollectionType
* - IVanillaReaderNavigationItem
*
**/
export interface IVanillaReaderNavigationCollection {
    type: NavigationCollectionType,
    children: IVanillaReaderNavigationItem[]
}

/*
* # IVanillaReaderNavigationItem
*
* This interface describes a more contextually rich representation of a navigation item that what you get from its
* Colibrio Framework counterpart `IReaderPublicationNavigationItem`. On this type you for example get access to timeline
* position and content location data.
*
* Because of the extra data this item is more expensive to build and therefore will benefit from being reused between
* sessions.
*
* ## RELATED TYPES
*
* - IReaderPublicationNavigationItem
* - IReaderPublicationNavigation
* - IVanillaReaderNavigationItem
*
**/
export interface IVanillaReaderNavigationItem {
    title: string,
    collectionType?: NavigationCollectionType,
    locator?: string,
    timelineStartPosition?: number,
    children: IVanillaReaderNavigationItem[]
}

/*
* # IVanillaReaderSyncMediaOptions
*
* This interface describes options for the sync media playback.
*
* ## RELATED TYPES
*
* - IVanillaReaderAppUiSettingsData
* - IVanillaReaderPublicationData
*
**/
export interface IVanillaReaderSyncMediaOptions {
    highlightColor?: string,
    volume?: number,
    rate?: number,
    rangePlaybackOptions?: IVanillaReaderSyncMediaRangePlaybackOptions,
    webSpeechTtsSynthesizer?: IVanillaTtsServiceProvider
}

/*
* # IVanillaReaderSyncMediaRangePlaybackOptions
*
* This interface describes options specific to range playback.
*
* ## RELATED TYPES
*
**/
export interface IVanillaReaderSyncMediaRangePlaybackOptions {
    rangeHighlightColor?: string,
    rangeHighlightVisible?: boolean,
    pauseOnRangeCanceled?: boolean;
    removeRangeOnEndReached?: boolean;
    seekBehaviour?: SyncMediaRangeSeekBehavior;
}

/*
* # IVanillaReaderTtsOptions
*
* This interface describes specific options for the TTS playback.
*
**/
export interface IVanillaReaderTtsOptions extends IVanillaReaderSyncMediaOptions {
    voiceName: string | undefined,
}

export interface IVanillaReaderShelfItemData extends IVanillaReaderPublicationData {
    latestReadingPositionData?: IVanillaReaderContentPositionData,
    starred?: boolean,
    categories?: string[],
    hasOfflineData?: boolean
}

/*
* # IVanillaReaderPublicationData
*
* This interface describes the complete serializable application state that is related to the VanillaReaderPublication.
* Its purpose is primarily to describe the storage model for the publication and care should be taken when changing
* this type in order to not break user data.
*
* This data is also sent back to the UI once the publication is loaded as metadata.
*
* This data is created by the `VanillaReaderPublication` class.
*
* ## RELATED TYPES
*
* - VanillaReaderPublication
* - IVanillaReaderAppState
* - IVanillaReaderDataStore
*
**/

export interface IVanillaReaderPublicationData {
    id: string,
    fileName: string,
    fileSourceUri: string,
    format: string,
    url: string,
    title?: string,
    isRtl?: boolean,
    isFixedLayout?: boolean,
    isScripted?: boolean,
    isAudiobook?: boolean,
    hasMoSyncMedia?: boolean,
    metadata?: IPublicationMetadataData,
    coverImageAsBase64?: string,
    language?: string,
}

export interface IVanillaReaderPublicationStateData extends IVanillaReaderPublicationData {
    latestReadingPositionData?: IVanillaReaderContentPositionData,
    bookmarks?: IVanillaReaderBookmarkData[],
    highlights?: IVanillaReaderHighlightData[],
    landmarkData?: IVanillaReaderPublicationLandmarkData | undefined,
    storageState?: IReaderPublicationStorageState,
    styleOptions?: IVanillaReaderPublicationStyleOptions,
    ttsOptions?: IVanillaReaderTtsOptions
    searchIndexData?: IPublicationSearchIndexData,
    navigationTreeData?: IVanillaReaderNavigationTreeData,
    timelineData?: string | undefined
}

/*
* # IVanillaReaderPublicationCacheData
*
* */
export interface IVanillaReaderPublicationCacheData {
    _id?: string,
    publicationId?: string,
    landmarkData?: IVanillaReaderPublicationLandmarkData | undefined,
    storageState?: IReaderPublicationStorageState,
    searchIndexData?: IPublicationSearchIndexData,
    navigationTreeData?: IVanillaReaderNavigationTreeData,
    timelineData?: string | undefined
}

/*
* # IVanillaReaderPublicationOptionsData
*
* */
export interface IVanillaReaderPublicationOptionsData {
    _id?: string,
    publicationId: string,
    styleOptions?: IVanillaReaderPublicationStyleOptions,
    ttsOptions?: IVanillaReaderTtsOptions
}


export interface IVanillaReaderPublicationStyleOptions {
    defaultFontFamily?: 'serif' | 'sans-serif' | 'monospace' | 'cursive' | 'fantasy';
    fontSet?: IVanillaReaderFontSet | null;
    fontSizeScaleFactor?: number;
    lineHeightScaleFactor?: number;
    pageMargins?: IRectMargins;
    palette?: IVanillaReaderPublicationStylePalette | null;
    textAlignment?: IPublicationStyleTextAlignmentOptions | null;
}

export interface IVanillaReaderPublicationStylePalette extends IPublicationStylePalette {

    isDark?: boolean;

    /**
     * The new foreground color for heavily saturated original foreground colors.
     */
    accent: string;

    /**
     * The background color value to use for the darkest original background color.
     */
    backgroundDark: string;

    /**
     * The background color value to use for the brightest original background color.
     */
    backgroundLight: string;

    /**
     * The foreground color value to use for the darkest original foreground color.
     */
    foregroundDark: string;

    /**
     * The foreground color value to use for the brightest original foreground color.
     */
    foregroundLight: string;
}

export interface IVanillaReaderFontFace {
    family: string;
    fontStretch?: string;
    fontStyle?: string;
    fontWeight?: string;
    mediaType: MediaType;
}

export interface IVanillaReaderFontSet {
    name: string;
    defaults: IPublicationStyleFontDefaults;
    fontFaces: IVanillaReaderFontFace[];
    selectors?: {
        family: string;
        selector: string;
    }[],
}

export interface IVanillaReaderFontSetAsset extends IVanillaReaderFontSet {
    fontFaces: IVanillaReaderFontFaceAsset[],
}

export interface IVanillaReaderFontFaceAsset extends IVanillaReaderFontFace {
    src: string;
    srcUrl?: string;
}

/*
* # IVanillaReaderAppState
*
* This interface describes the complete serializable application state including the `IVanillaReaderSyncMediaOptions` and
* the `IVanillaReaderViewOptions`. This is all the data that is needed to restore a previous session.
*
* This data is created by the `VanillaReader` class.
*
* ## RELATED TYPES
*
* - IVanillaReaderViewOptions
*
**/
export interface IVanillaReaderOptionsData {
    _id?: number,
    dateCreated?: number,
    viewOptions?: IVanillaReaderViewOptions,
    syncMediaOptions?: IVanillaReaderSyncMediaOptions
}

/*
* # IVanillaReaderViewOptions
*
* This interface describes the all the serializable application state related to rendering specific features, such as
* what IRenderer, what options to use for that renderer instance etc.
*
* This data is created by the `VanillaReader` class.
*
* ## RELATED TYPES
*
* - IVanillaReaderAppState
* - IRendererOptions
* - IReaderViewOptions
*
**/
export interface IVanillaReaderViewOptions {
    _id?: number,
    colibrioRendererOptions?: IRendererOptions,
    colibrioReaderViewOptions?: IReaderViewOptions,
    activeRendererTypeName?: VanillaReaderRendererNames
    ignoreAspectRatio?: boolean,
    useReducedMotion?: boolean,
    showPageRangesLayer?: boolean,
    scrollOverflowingTables?: boolean,
}

/*
* # IVanillaReaderInitialOptions
*
* This interface describes data that can be used to not only restore a previous application session using saved
* `IVanillaReaderAppState` state, but also take in possible additional parameters. `initialUrl` for example tells
* the VanillaReader to load the specified url as it boots up. `slowNetwork` is also a helpful parameter that the
* application start up code (index.ts) can send to the VanillaReader so that it may optimise user experience accordingly.
*
* This data is created in the `index.ts` file.
*
* ## RELATED TYPES
*
* - IVanillaReaderAppState
*
**/
export interface IVanillaReaderInitialOptions {
    slowNetwork?: boolean;
    storeOnDevice?: boolean;
    viewOptions?: IVanillaReaderViewOptions;
    syncMediaOptions?: IVanillaReaderSyncMediaOptions;
    customReaderViewContentOnLoading?: string;
    customReaderViewContentOnLoadError?: string;
}

/*
* # IVanillaReaderVisibleRangeData
*
* This interface describes data for the currently visible content. It is sent as event data in the `IVanillaReaderAppEvent_VISIBLE_RANGE_CHANGED`
* app event.
*
* ## RELATED TYPES
*
* - `IVanillaReaderAppEvent_VISIBLE_RANGE_CHANGED`
*
**/
export interface IVanillaReaderVisibleRangeData {
    locator?: ILocator,
    timelinePosition?: number | undefined,
    timelineRange?: IIntegerRange | undefined,
    readingProgression?: number | undefined,
    visibleTextContent?: string,
    bookmarks: IVanillaReaderBookmarkData[],
    highlights: IVanillaReaderHighlightData[],
    navItems: IVanillaReaderNavigationItem[],
    documentIsAtStart: boolean,
    documentIsAtEnd: boolean,
    publicationIsAtStart: boolean,
    publicationIsAtEnd: boolean,
    visibleContentRects?: IVisibleContentRectData[],

}

/*
* # IVanillaReaderReadingPositionData
*
**/
export interface IVanillaReaderContentPositionData {
    _id?: string | undefined,
    publicationId?: string | undefined,
    contentBlockData?: IContentBlockData,
    contentRects?: IVisibleContentRectData[],
    locator: string | undefined,
    navItemData?: IVanillaReaderNavigationItem[],
    documentIndex?: number,
    syntheticPageData?: IVanillaReaderSyntheticPageData[],
    timestamp: number,
}

/*
* # VanillaReaderPublicationLandmarkTypeNote
*
**/

export enum VanillaReaderPublicationLandmarkTypeNote {
    FOOTNOTE = 'FOOTNOTE',
    REARNOTE = 'REARNOTE',
    ENDNOTE = 'ENDNOTE'
}

/*
* # VanillaReaderPublicationLandmarkType
*
**/

export enum VanillaReaderPublicationLandmarkType {
    ASIDE = ContainerContentBlockType.ASIDE,
    ARTICLE = ContainerContentBlockType.ARTICLE,
    FIGURE = ContainerContentBlockType.FIGURE,
    TABLE = ContainerContentBlockType.TABLE,
    SECTION = ContainerContentBlockType.SECTION,
    DESCRIPTION_LIST = ContainerContentBlockType.DESCRIPTION_LIST,
    ORDERED_LIST = ContainerContentBlockType.ORDERED_LIST,
    UNORDERED_LIST = ContainerContentBlockType.UNORDERED_LIST,
    IMAGE = MediaContentBlockType.IMAGE,
    AUDIO = MediaContentBlockType.AUDIO,
    VIDEO = MediaContentBlockType.VIDEO,
    HEADING1 = TextContentBlockType.HEADING1,
    HEADING2 = TextContentBlockType.HEADING2,
    HEADING3 = TextContentBlockType.HEADING3,
    HEADING4 = TextContentBlockType.HEADING4,
    HEADING5 = TextContentBlockType.HEADING5,
    HEADING6 = TextContentBlockType.HEADING6,
    FOOTNOTE = VanillaReaderPublicationLandmarkTypeNote.FOOTNOTE,
    REARNOTE = VanillaReaderPublicationLandmarkTypeNote.REARNOTE,
    ENDNOTE = VanillaReaderPublicationLandmarkTypeNote.ENDNOTE,
}

/*
* # IVanillaReaderPublicationLandmarkData
*
**/
export interface IVanillaReaderPublicationLandmarkData {
    _id?: number,
    publicationId: string,
    documents: IVanillaReaderDocumentLandmarkData[],
}


export interface IVanillaReaderPublicationLandmarkCollectionData {
    headings?: IVanillaReaderLandmarkData[],
    tables?: IVanillaReaderLandmarkData[],
    figures?: IVanillaReaderLandmarkData[],
    audio?: IVanillaReaderLandmarkData[],
    video?: IVanillaReaderLandmarkData[],
    lists?: IVanillaReaderLandmarkData[],
    sections?: IVanillaReaderLandmarkData[],
    articles?: IVanillaReaderLandmarkData[],
    asides?: IVanillaReaderLandmarkData[],
    footnotes?: IVanillaReaderLandmarkData[],
}


/*
* # IVanillaReaderLandmark
*
**/

export interface IVanillaReaderDocumentLandmark {
    documentIndex: number,
    headings?: VanillaReaderLandmark[],
    tables?: VanillaReaderLandmark[],
    figures?: VanillaReaderLandmark[],
    audio?: VanillaReaderLandmark[],
    video?: VanillaReaderLandmark[],
    lists?: VanillaReaderLandmark[],
    sections?: VanillaReaderLandmark[],
    articles?: VanillaReaderLandmark[],
    asides?: VanillaReaderLandmark[],
    footnotes?: VanillaReaderLandmark[],
}

export interface IVanillaReaderPublicationLandmark {
    id?: number,
    publicationId: string,
    documents: IVanillaReaderDocumentLandmark[],
}

export interface IVanillaReaderPublicationLandmarkCollection {
    headings?: VanillaReaderLandmark[],
    tables?: VanillaReaderLandmark[],
    figures?: VanillaReaderLandmark[],
    audio?: VanillaReaderLandmark[],
    video?: VanillaReaderLandmark[],
    lists?: VanillaReaderLandmark[],
    sections?: VanillaReaderLandmark[],
    articles?: VanillaReaderLandmark[],
    asides?: VanillaReaderLandmark[],
    footnotes?: VanillaReaderLandmark[],
}


/*
* # IVanillaReaderDocumentLandmarkData
*
**/

export interface IVanillaReaderDocumentLandmarkData {
    documentIndex: number,
    headings?: IVanillaReaderLandmarkData[],
    tables?: IVanillaReaderLandmarkData[],
    figures?: IVanillaReaderLandmarkData[],
    audio?: IVanillaReaderLandmarkData[],
    video?: IVanillaReaderLandmarkData[],
    lists?: IVanillaReaderLandmarkData[],
    sections?: IVanillaReaderLandmarkData[],
    articles?: IVanillaReaderLandmarkData[],
    asides?: IVanillaReaderLandmarkData[],
    footnotes?: IVanillaReaderLandmarkData[],
}

export interface IVanillaReaderLandmarkData extends IContentBlockData {
    timelinePosition: number | undefined;
    subType?: VanillaReaderPublicationLandmarkType;
    description?: string;
    locatorUrl?: string;
    navigationItem?: IVanillaReaderNavigationItem;
    documentIndex?: number;
}

export interface IVanillaReaderUiColorTheme extends IVanillaReaderPublicationStylePalette {
    name: string;
    uiIsDark: boolean;
    uiOutlineDark?: string;
    uiOutlineLight?: string;
    uiBackgroundDark?: string;
    uiBackgroundLight?: string;
    uiForegroundDark?: string;
    uiForegroundLight?: string;
}

/*
* # IVanillaSyncMediaPlaybackStateData
*
* This interface describes the runtime state of the `ISyncMediaTimeline` during playback. It is sent in various app
* events such as `IVanillaReaderAppEvent_MEDIAPLAYER_PLAYBACK_STATE_CHANGED` and `IVanillaReaderAppEvent_MEDIAPLAYER_PLAYBACK_POSITION_CHANGED`.
*
* ## RELATED TYPES
*
* - VanillaReader
* - VanillaReaderAudiobookPlayer
* - VanillaReaderMediaPlayer
*
* */
export interface IVanillaSyncMediaPlaybackStateData {
    position: number,
    type?: string,
    positionWithinSegment?: number,
    isSeeking?: boolean,
    isPaused?: boolean,
    isMuted?: boolean,
    rate?: number,
    volume?: number,
    navigationItems?: IVanillaReaderNavigationItem[],
    locator?: string,
    segmentIndex?: number,
    segmentData?: IVanillaSyncMediaSegmentData,
    publicationDurationMs?: number,
    playbackRangeData?: IVanillaReaderPlaybackRangeData | undefined,
}

export interface IVanillaReaderPlaybackRangeData {
    // isCanceled?: boolean,
    // isAtEnd?: boolean,
    // cancellationReason?: SyncMediaRangePlaybackCancelReason,
    id: number,
    range: ISyncMediaTimelineRangeData,
    options?: IVanillaReaderSyncMediaRangePlaybackOptions,
}

export interface IVanillaSyncMediaSegmentData extends ISyncMediaSegmentData {
    segmentTimelineStartOffsetMs?: number
}

/*
* # IVanillaReaderReadingProgressionTimeline
*
* A common interface to describe the reading progression in a reader publication using a numeric index.
*
* It is used to hide the differences between the `IContentPositionTimeline` in text based book and the `ISyncMediaTimeline`
* used in audiobooks.
*
* This is a convenience abstraction that is very implementation specific to the VanillaReader. Your own implementation
* may choose another strategy.
*
* ## RELATED TYPES
*
* - VanillaReaderAudiobookProgressionTimeline
* - VanillaReaderProgressionTimeline
* - IContentPositionTimeline
* - ISyncMediaTimeline
*
**/
export interface IVanillaReaderReadingProgressionTimeline {
    fetchTimelinePosition: (location: ILocator) => Promise<number | undefined>;
    fetchContentLocation: (timelinePosition: number) => Promise<IContentLocation | undefined>;
    fetchContentLocationAsRange: (start: number, end: number) => Promise<IContentLocation | undefined>;
    fetchTimelineRange: (location: ILocator) => Promise<IIntegerRange | undefined>;
    getLength: () => number;
    isReady: () => boolean;
    getBackingTimelineImplementation: () => IContentPositionTimeline | IPageProgressionTimeline | ISyncMediaTimeline;
    toSerializedData: () => string | undefined;
    updateTimelinePosition: (locator: ILocator) => void;
    onTimelinePositionUpdated: (callback: VanillaProgressEventCallback) => void;
    onTimelineReady: (callback: VanillaTimelineReadyEventCallback) => void;
    serialize: () => Promise<IVanillaReaderReadingProgressionTimelineData | undefined>;
}

export interface IVanillaReaderReadingProgressionTimelineData {
    length: number,
    unit: ContentPositionTimelineUnit | undefined,
    sections?: IVanillaReaderReadingProgressionTimelinePositionData[],
}

export interface IVanillaReaderReadingProgressionTimelinePositionData {
    range: IIntegerRange,
    contentPositionData: IVanillaReaderContentPositionData
}

export interface IVanillaReaderEscapableContentBlockContext {
    blockId: number,
    block: IContentBlock,
    blockRangeLocation: IContentLocation
}

export interface IVanillaReaderEscapableContentBlockDataContext {
    blockId: number,
    blockData: IContentBlockData,
    blockStartLocator: ILocatorData
    blockEndLocator: ILocatorData
}


export interface IVanillaReaderUserProfileData {
    id: string;
    token: string;
}

export enum VanillaReaderPaginationType {
    SECTIONS = 'sections',
    PAGE_LIST = 'pagelist',
    SYNTHETIC = 'synthetic',
    ESTIMATED = 'estimated'
}

export interface IVanillaReaderTimelineProgressData {
    pageData: IVanillaReaderSyntheticPageData | undefined;
    pageProgress: number | undefined;
    timelineProgress: number;
    timelineValue: number;
}

export interface IVanillaReaderSyntheticPageData {
    label: string;
    index: number;
    range: IntRange;
    section: IntRange | undefined;
    locatorData?: ILocatorData | undefined;
    navItems?: IVanillaReaderNavigationItem[] | undefined;
}

export interface IVanillaReaderSyntheticPageListData {
    pages: IVanillaReaderSyntheticPageData[];
    paginationType: VanillaReaderPaginationType;
    syntheticPaginationSegmentationMethod: VanillaReaderSyntheticPaginationSegmentationMethod | undefined;
}

export enum VanillaReaderSyntheticPaginationSegmentationMethod {
    // All these codepoints are the ones in Unicode General_Category=Z (Zs | Zl | Zp) except FEFF
    UNICODE_WHITESPACE_SEGMENTATION = 'unicode_whitespace_segmentation',
    // https://unicode.org/reports/tr29/#Table_Word_Break_Property_Values
    UNICODE_DEFAULT_WORD_BREAK_SEGMENTATION = 'unicode_default_word_break_segmentation'
}

export interface IVanillaReaderNavigationAction {
    navigationType: VanillaReaderNavigationType,
    externalNavigation?: boolean,
    locator?: string,
    position?: number,
    url?: string,
    focusNearContentLocation?: boolean
}

export interface IVanillaReaderTransformData {
    transformData: ITransformData | undefined;
    visiblePageClientRects: IRect[];
    zoomTargetClientRect: IRect | undefined;
    publicationViewportScale: number;
}

export interface IVanillaReaderGuidedReadingReadingOptions {
    defaultTransitionOptions: ITransformRectZoomOptions;
    maskPageContent: boolean;
}

export interface IVanillaReaderGuidedReadingTimelineData {
    publicationId: string;
    sequences: IVanillaReaderGuidedReadingSequenceData[];
}
export interface IVanillaReaderGuidedReadingSequenceData {
    locator: string;
    steps: IVanillaReaderGuidedReadingStepData[];
}
export interface IVanillaReaderGuidedReadingStepData {
    rect: IRect;
    locator: string;
    transitionOptions?: ITransformRectZoomOptions;
}

export interface IVanillaReaderGuidedReadingReadingPositionData {
    publicationId: string;
    sequence: IVanillaReaderGuidedReadingSequenceData;
    step: IVanillaReaderGuidedReadingStepData;
    time: number
}

export interface IVanillaTtsServiceProvider extends ITtsSynthesizer, EventListenerObject {
    supportsWordBoundaries: boolean;
    setActiveVoice(voice: SpeechSynthesisVoice | undefined): void;
    getActiveVoice(): SpeechSynthesisVoice | undefined;
    fetchVoices(): Promise<ISpeechSynthesisVoiceCollection | undefined>;
}

// CALLBACK TYPES

export type VanillaNavigationStartedEventCallback = (ev: INavigationStartedEngineEvent) => void;
export type VanillaNavigationEndedEventCallback = (ev: INavigationEndedEngineEvent) => void;
export type VanillaReaderViewEventCallback = (ev: IReaderViewEngineEvent) => void;
export type VanillaPageProgressionTimelineRecalculatedCallback = (ev: IPageProgressionTimelineEngineEvent) => void;
export type VanillaNavigationIntentEventCallback = (ev: INavigationIntentEngineEvent) => void;
export type VanillaBookmarkReadingPositionIntentEventCallback = () => void;
export type VanillaSelectionChangedEventCallback = (ev: ISelectionChangedEngineEvent) => void;
export type VanillaPointerEngineEventCallback = (ev: IPointerEngineEvent, contentPositionData?: IVanillaReaderContentPositionData) => void;
export type VanillaMouseEngineEventCallback = (ev: IMouseEngineEvent) => void;
export type VanillaMouseEventCallback = (ev: MouseEvent) => void;
export type VanillaPointerEventCallback = (ev: PointerEvent) => void;
export type VanillaViewTransformChangedCallback = (transformState: ITransformData | null) => void;
export type VanillaZoomToolDragCallback = (ev: PointerEvent, rect?: DOMRect) => void;
export type VanillaActiveRendererChangedEventCallback = (ev: IActiveRendererChangedEngineEvent) => void;
export type VanillaKeyboardEventCallback = (ev: IKeyboardEngineEvent) => void;
export type VanillaAnnotationMouseEventCallback = (ev: IReaderViewAnnotationMouseEngineEvent) => void;
export type VanillaAnnotationPointerEventCallback = (ev: IReaderViewAnnotationPointerEngineEvent) => void;
export type VanillaAnnotationEventCallback = (ev: IReaderViewAnnotationEngineEvent) => void;
export type VanillaAnnotationCollectionChangedEventCallback = () => void;
export type VanillaTimelineReadyEventCallback = (timelineData: IVanillaReaderReadingProgressionTimelineData) => void;
export type VanillaProgressEventCallback = (progress: number, resourceId?: string) => void;
export type VanillaHighlightDataCallback = (highlight: IVanillaReaderHighlightData, silent?: boolean) => void;
export type VanillaBookmarkDataCallback = (bookmark: IVanillaReaderBookmarkData, silent?: boolean) => void;
export type VanillaOpenFileCallback = (fileObject: File) => void;
export type VanillaOpenUrlCallback = (url: string) => void;
export type VanillaVoidCallback = () => void;
export type VanillaReaderViewOptionsCallback = (options: IVanillaReaderViewOptions) => void;
export type VanillaPublicationDataCallback = (publicationData: IVanillaReaderPublicationData) => void;
export type VanillaPublicationStateDataCallback = (publicationData: IVanillaReaderPublicationStateData) => void;
export type VanillaPublicationCacheDataCallback = (publicationCacheData: IVanillaReaderPublicationCacheData) => void;
export type VanillaPublicationOptionsDataCallback = (publicationOptionsData: IVanillaReaderPublicationOptionsData) => void;
export type VanillaStringValueCallback = (value: string) => void;
export type VanillaNumberValueCallback = (value: number) => void;
export type VanillaBooleanValueCallback = (value: boolean) => void;
export type VanillaDialogOpenedCallback = (dialogInstance: VanillaReaderUiDialog<any>, dialogName: string, silent: boolean) => void;
export type VanillaDialogClosedCallback = (dialogInstance: VanillaReaderUiDialog<any>, dialogName: string, silent: boolean) => void;
export type VanillaMenuHiddenCallback = (menuName: string, silent: boolean) => void;
export type VanillaAppStateUpdatedCallback = (appState: IVanillaReaderOptionsData) => void;
export type VanillaMediaPlayerPlaybackStateChangedCallback = (state: IVanillaSyncMediaPlaybackStateData) => void;
export type VanillaMediaPlayerPlaybackRangeRemovedCallback = (state: IVanillaSyncMediaPlaybackStateData, reason?: SyncMediaRangePlaybackRemoveReason) => void;
export type VanillaPublicationShelfUpdatedCallback = (shelfItems: VanillaReaderShelfItem[]) => void;
export type VanillaPublicationShelfItemCallback = (shelfItem: VanillaReaderShelfItem) => void;
export type VanillaZoomToLevelCallback = (zoomLevel: number, animate?: boolean) => void;
export type VanillaZoomToRectCallback = (zoomRect: IRect, transformOptions: ITransformRectZoomOptions) => void;
export type VanillaReadingRulerDragCallback = (ev: PointerEvent, intersectingRectData: IVisibleContentRectData) => void;
export type VanillaReadingRulerMoveCallback = (intersectingRectData: IVisibleContentRectData, affectReadingPosition?: boolean) => void;
export type VanillaProgressionSliderCallback = (sliderValue: number, progressData: IVanillaReaderTimelineProgressData) => void;
export type VanillaGuidedReadingStepCallback = (step: VanillaReaderGuidedReadingStep) => void;
export type VanillaContentPositionCallback = (contentPositionData: IVanillaReaderContentPositionData) => void;
export type VanillaSwipeNavigationProgressCallback = (eventData: ISwipeNavigationGestureProgressEngineEventData) => void;
export type VanillaMediaPlayerEscapabilityContextCallback = (context: IVanillaReaderEscapableContentBlockContext[]) => void;



